
import { computed, defineComponent, nextTick, ref, toRef, watch } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { TranslationService } from "@/core/services/TranslationService";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { FormApi } from "@/services/api/Form.service";
import { SiteApi } from "@/services/api/Site.service";
import * as Yup from "yup";
import { ICloneForm } from "@/interfaces/iForm";
import { hideModal } from "@/core/helpers/dom";

import { useToast } from "vue-toastification";
import VZMultiSelect from "@/components/modals/forms/VZMultiSelect.vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { getLookups } from "@/services/api/Lookup.service";
import moment from "moment-timezone";
export default defineComponent({
  name: "form-clone-modal",
  emit: ["clone-submit"],
  props: {
    form: { type: Object, default: null },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
    VZMultiSelect,
    Datepicker,
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const submitButton = ref<HTMLButtonElement | null>(null);
    const cloneSuccessMsg = t("pages.form.formCloneSuccess");
    const newTargetCloneModalRef = ref<null | HTMLElement>(null);
    const cloneFormRef = ref<null | HTMLFormElement>(null);
    const loading = ref(false);
    const siteValidation = ref(false);
    let formObj = ref<any>(props.form);
    const cloneData = toRef(props, "form");
    let formClone: ICloneForm;
    let cloneTargetData = ref<any>({});
    const userList = ref<any>([]);
    let categories = ref<{ label: any; value: string }[]>([]);
    let cloneFormData = ref({
      validationSchema: Yup.object({
        name: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
        categories: Yup.array()
          .nullable()
          .min(1, t("pages.form.category") + " " + t("common.isRequired"))
          .required(t("pages.form.category") + " " + t("common.isRequired")),
        // frequency: Yup.string()
        //   .nullable()
        //   .required(t("pages.form.frequency") + " " + t("common.isRequired")),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .min(Yup.ref("startDate"), t("validation.greaterEndDate"))
          .nullable(),
      }),
    });
    formObj.value = computed(() => {
      return props.form;
    }).value;
    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "MM-dd-yyyy";
    });
    const discard = () => {
      const accordions = document.getElementsByClassName(
        "accordion-button fs-5 fw-bold text-black-50"
      );
      const accorBody = document.getElementsByClassName("accor-body");
      for (let i = 0; i < accordions.length; i++) {
        accordions[i].className =
          "accordion-button fs-5 fw-bold text-black-50 collapsed";
      }
      for (let i = 0; i < accorBody.length; i++) {
        accorBody[i].className = "accordion-collapse collapse accor-body";
      }
      var appElement = document.getElementById("app");

      if (appElement) {
        appElement.click();
      }
      hideModal(newTargetCloneModalRef.value);
      nextTick(() => {
        clearFormData();
        cloneFormRef?.value?.resetForm();
      });
      emit("on-cancel", null);
    };

    const format = computed(() => {
      return FormatMethods;
    });

    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(formObj.value.startDate).format("YYYY-MM-DD");
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });

    const formEventsList = ref<any>([]);

    const setFormCloneData = async () => {
      cloneFormData.value["categories"] = [];
      cloneFormData.value["startDate"] = formObj.value.startDate;
      cloneFormData.value["endDate"] = formObj.value.endDate;
      cloneFormData.value["locationRestrict"] = false;
      cloneFormData.value["sequence"] = false;
      categories.value = [];
      try {
        loading.value = true;
        const [
          categoriesList,
          sectionList,
          siteList,
          eventList,
          userList1,
          lookup,
        ] = await Promise.all([
          FormApi.getInstance().getCategoriesList(),
          FormApi.getInstance().getFormSectionList({
            status: "ACTIVE",
            size: 100000,
            formDefinitionId: formObj.value.webId,
          }),
          SiteApi.getInstance().getFormSitesList(formObj.value.webId, {
            size: 100000,
            status: "ACTIVE",
          }),
          FormApi.getInstance().getFormEventList(formObj.value.webId, {
            size: 100000,
            status: "ACTIVE",
          }),
          FormApi.getInstance().getFormUser({
            size: 100000,
            formDefinitionId: formObj.value.webId,
          }),
          getLookups(
            "static-data/filter/LOOKUP_FILTER/filters?filter[lookupName]=LKFormsFrequency"
          ),
        ]);
        categoriesList.map((obj: any) => {
          categories.value.push({
            label: obj.text,
            value: obj.value,
          });
        });
        cloneFormData.value["cloneSections"] = sectionList.content;
        cloneFormData.value["cloneSites"] = siteList.content;
        cloneFormData.value["cloneEvents"] = eventList.content;
        formEventsList.value = eventList.content;
        cloneFormData.value["cloneUsers"] = userList1.content;
        userList.value = userList1.content;

        cloneTargetData.value["cloneSections"] = sectionList.content.map(
          (obj: any) => {
            return obj.formSectionId;
          }
        );

        cloneTargetData.value["cloneSites"] = siteList.content.map(
          (obj: any) => {
            return obj.webId;
          }
        );
        cloneTargetData.value["cloneEvents"] = eventList.content.map(
          (obj: any) => {
            return obj.eventId;
          }
        );
        cloneTargetData.value["cloneUsers"] = userList1.content.map(
          (obj: any) => {
            return obj.webId;
          }
        );

        cloneFormData.value["frequency"] = lookup.map((obj: any) => {
          return {
            label: obj.text,
            value: obj.value,
          };
        });
      } catch (error) {
        //
      } finally {
        loading.value = false;
      }
    };
    const submitCloneData = () => {
      formClone = {} as ICloneForm;
      formClone.startDate = formObj.value.startDate;
      formClone.endDate = formObj.value.endDate;
      formClone.name = cloneTargetData.value.name;
      formClone.frequency = cloneTargetData.value.frequency;
      formClone.locationRestrict =
        cloneTargetData.value.locationRestrict == true;
      formClone.sequence = cloneTargetData.value.sequence == true;
      // formClone.status = "ACTIVE";
      // find selected formSectionIds in cloneFormData.value["cloneSections"] and push into formClone.formSectionGlobalModel
      const formSectionLocationModels =
        cloneTargetData?.value?.["cloneSections"]?.map(
          (sectionId: any, index: number) => {
            const section = cloneFormData.value["cloneSections"]?.find(
              (section: any) => section.formSectionId == sectionId
            );
            if (section) {
              return {
                sectionId: section.formSectionId,
                locationId: section?.locationId,
                sequence: index,
              };
            }
          }
        ) ?? {};
      formClone.formSectionGlobalModel = {
        formSectionLocationModels,
      };
      formClone.formCategoryGlobalModel = {
        categoryCode:
          cloneTargetData?.value?.["categoryCode"]?.length > 0
            ? cloneTargetData?.value?.["categoryCode"]
            : [],
      };
      formClone.formSiteGlobalModel = {
        siteIds:
          cloneTargetData?.value?.["cloneSites"]?.length > 0
            ? cloneTargetData?.value?.["cloneSites"]
            : [],
      };
      formClone.formUserGlobalModel = {
        formUsers:
          cloneTargetData?.value?.["cloneUsers"]?.length > 0
            ? userList.value
                .filter((obj) =>
                  cloneTargetData.value["cloneUsers"].includes(obj.webId)
                )
                .map(({ userId, siteId, roleId }) => {
                  if (userId && userId != -1) {
                    return {
                      userId: userId === -1 ? roleId : userId,
                      siteId: siteId,
                    };
                  } else {
                    return {
                      roleId: roleId,
                      siteId: siteId,
                    };
                  }
                })
            : [],
      };
      // formClone.formEventGlobalModel = {
      //   eventIds:
      //     cloneTargetData?.value?.["cloneEvents"]?.length > 0
      //       ? cloneTargetData?.value?.["cloneEvents"]
      //       : [],
      // };
      formClone.formRoleGlobalModel = {
        roleIds: [],
      };
      const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
      const startMoment = moment(formObj.value.startDate).startOf("day");
      const formattedStartDate = startMoment.format(dateFormat);
      formClone.startDate = formattedStartDate;

      if (formObj.value.endDate) {
        const endMoment = moment(formObj.value.endDate).endOf("day");
        const formattedEndDate = endMoment.format(dateFormat);
        formClone.endDate = formattedEndDate;
      }
      if (!siteValidation.value) {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          // Activate indicator
          submitButton.value.setAttribute("data-kt-indicator", "on");
        }
        let formEventCloneGlobalModel = [];

        if (cloneTargetData.value?.cloneEvents?.length > 0) {
          const selectedEvents = formEventsList.value.filter((event: any) =>
            cloneTargetData.value.cloneEvents.includes(event.eventId)
          );
          formEventCloneGlobalModel = selectedEvents.map((event: any) => ({
            userId: event.userId ? event.userId : event.roleId,
            siteId: event.siteId,
            eventId: event.eventId,
          }));
        }
        const formattedFormClone = {
          clone: true,
          ...formClone,
          formEventCloneGlobalModel,
        };

        FormApi.getInstance()
          .submitCloneForm(formattedFormClone)
          .then(() => {
            toast.success(cloneSuccessMsg);
          })
          .catch(() => {
            // Error
          })
          .then(() => {
            discard();
            emit("clone-submit");
          })
          .finally(() => {
            if (submitButton.value) {
              // eslint-disable-next-line
              submitButton.value!.disabled = false;
              // Deactivate indicator
              submitButton.value.removeAttribute("data-kt-indicator");
            }
          });
      }
    };

    const clearFormData = () => {
      cloneFormRef?.value?.resetForm();
      formObj.value.startDate = null;
      formObj.value.endDate = null;
      cloneTargetData.value = {};
    };

    const selectAll = (element, array) => {
      if (element.srcElement.checked) {
        cloneTargetData.value[array] = cloneFormData.value[array]?.map(
          (obj) => {
            if (array == "cloneUsers") {
              return obj.webId;
            } else if (array == "cloneSections") {
              return obj.formSectionId;
            }
            return obj.webId;
          }
        );
      } else {
        cloneTargetData.value[array] = [];
      }
    };
    const getClass = (role) => {
      let className = "bg-success";
      if (role == "Manager") {
        className = "bg-info";
      } else if (role == "User") {
        className = "bg-primary";
      } else if (role == "Admin") {
        className = "bg-success";
      }
      return className;
    };

    const isChecked = (array, id) => {
      if (cloneTargetData?.value?.["cloneSites"]?.length <= 0) {
        siteValidation.value = true;
      } else {
        siteValidation.value = false;
      }
      return (
        cloneTargetData?.value[array]?.filter((objId) => objId == id).length > 0
      );
    };

    const updateCloneArray = (element, id, objectId) => {
      if (element.srcElement.checked) {
        if (
          cloneTargetData?.value[objectId] == undefined ||
          cloneTargetData?.value[objectId].length == 0
        ) {
          cloneTargetData.value[objectId] = [];
        }
        cloneTargetData?.value[objectId].push(id);
      } else {
        cloneTargetData.value[objectId] = cloneTargetData?.value[
          objectId
        ].filter((objId) => objId != id);
      }
    };
    watch(
      cloneData,
      (newValue) => {
        formObj.value = { ...newValue };
        clearFormData();
        setFormCloneData();
      },
      {
        immediate: true,
      }
    );

    return {
      t,
      setFormCloneData,
      cloneFormData,
      submitCloneData,
      cloneTargetData,
      getClass,
      updateCloneArray,
      newTargetCloneModalRef,
      format,
      cloneFormRef,
      discard,
      formObj,
      isChecked,
      selectAll,
      displayDate,
      loading,
      categories,
      submitButton,
      siteValidation,
      userList,
      minEndDate,
      formEventsList,
    };
  },
});
